import { useMemo } from "react";
import {
  DropdownSearch,
  type DropdownOption,
  type DropdownSearchProps,
} from "ui";
import { useField } from "../hooks/useField";
import { useFormFieldContext } from "./FormField";

type DropdownSearchFormFieldProps<TLabel> = {
  multiple?: boolean;
  className?: string;
  label?: string;
  renderOption?: (props: TLabel) => React.ReactNode;
  displayedValueKey?: string;
  displayedValue?: DropdownSearchProps["displayedValue"];
  contentProps?: DropdownSearchProps["contentProps"];
  isItemDisabled?: DropdownSearchProps["isItemDisabled"];
  placeholder?: string;
  disableEmptySearch?: boolean;
  disableSearch?: boolean;
} & (
  | {
      options: DropdownOption<TLabel>[];
      getOptions?: never;
    }
  | {
      options?: never;
      getOptions?: (query: string) => Promise<DropdownOption<TLabel>[]>;
    }
);

export function DropdownSearchFormField<TLabel = string>({
  label,
  options,
  multiple,
  renderOption,
  displayedValueKey,
  displayedValue,
  className,
  contentProps,
  getOptions,
  placeholder,
  isItemDisabled,
  disableEmptySearch,
  disableSearch,
}: DropdownSearchFormFieldProps<TLabel>) {
  const {
    name,
    disabled,
    readOnly,
    required,
    defaultValue,
    validation,
    customValidation,
    descriptionId,
    errorMessageId,
    label: contextLabel,
  } = useFormFieldContext();

  const { ref, value, onChange, onBlur, error } = useField({
    name,
    defaultValue: defaultValue || (multiple ? [] : ""),
    validation,
    customValidation,
  });

  const selectedOptions = useMemo(() => {
    if (!options?.length) return [];
    if (multiple) {
      return (
        value
          ?.map?.((v: string) =>
            options.find((option) => option[0] === String(v))
          )
          ?.filter((v: unknown) => v !== undefined) || []
      );
    }

    return options.find((option) => option[0] === String(value)) || "";
  }, [value, multiple, options]);

  return (
    // @ts-ignore
    <DropdownSearch<TLabel>
      triggerRef={ref}
      id={name}
      name={name}
      label={label ?? `Select ${contextLabel}`}
      className={className}
      options={options}
      selected={getOptions ? (value as [string, string][]) : selectedOptions}
      onSelect={(value: DropdownOption<TLabel>[]) => {
        if (getOptions) {
          // NOTE: lazy loaded options from BE should have the same shape of DropdownOption<TLabel>[]
          // it can't be transformed to use the first value of the option as the value
          // selected should be as ["<id>", "<name>"] and can't be as "<id>"
          return onChange(value);
        }

        if (multiple) {
          onChange(value.map((option: [string, any]) => option[0]));
        } else {
          onChange(value[0] || "");
        }
      }}
      onBlur={onBlur}
      error={!!error}
      multiple={multiple}
      disabled={disabled || readOnly}
      renderOption={renderOption!}
      displayedValueKey={displayedValueKey!}
      displayedValue={displayedValue}
      aria-required={required}
      aria-describedby={
        !error ? `${descriptionId}` : `${descriptionId} ${errorMessageId}`
      }
      aria-invalid={!!error}
      resettable
      contentProps={contentProps}
      getOptions={getOptions}
      placeholder={placeholder}
      isItemDisabled={isItemDisabled}
      disableEmptySearch={disableEmptySearch}
      disableSearch={disableSearch}
    />
  );
}
